import { Component, input, ElementRef, ViewEncapsulation, model, viewChild, output, inject, effect } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ControlValueAccessor, NgControl, ValidatorFn, Validators, FormsModule } from '@angular/forms';
import { noop } from 'rxjs';

import { ApContentService } from '@ap/content';
import { AvailableIcons, ApIconComponent } from '@ap/icon';
import { ErrorMessages, ApFormErrorsComponent } from '@ap/form/errors';
import { ApContentPipe } from '@ap/content';
import { HelpText, HelpTextPosition } from '@ap/form';

import content from './textarea.content.json';
import { ApAutoGrowDirective } from './auto-grow/auto-grow.directive';

/**
 * @ngdoc component
 * @name ApTextareaComponent
 * @description Renders the text area component
 *
 */
@Component({
    selector: 'ap-textarea',
    templateUrl: './textarea.component.html',
    styleUrls: ['./textarea.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgClass, FormsModule, ApAutoGrowDirective, ApIconComponent, ApFormErrorsComponent, ApContentPipe, NgTemplateOutlet]
})
export class ApTextareaComponent implements ControlValueAccessor, HelpText {
    protected control = inject(NgControl, { self: true, optional: true })!;
    protected readonly contentService = inject(ApContentService);

    /**
     * Maximum allowed characters. Shows the countdown element in the top/right when typing.
     */
    public readonly maxCharacters = input<number>();

    /**
     * Field label. Shown in the field. Moves up if focussed or filled in.
     */
    public readonly label = input<string>();

    /**
     * Description of field. Shown above the field.
     */
    public readonly description = input<string>();

    /**
     * Error message object or array of strings.
     * If array of strings: First string is always shown on error
     * If error message object: Key that matches current error is shown.
     */
    public readonly messages = model<ErrorMessages | string[]>({});

    /**
     * Help text shown under field. Disappears if errors are present.
     */
    public readonly helpText = input<string>();

    /**
     * Help text position, either 'below' (default) or 'above' the input field.
     */
    public readonly helpTextPosition = model<HelpTextPosition>(HelpTextPosition.BELOW);

    /**
     * Expose `HelpTextPosition` enum for template
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected readonly HelpTextPosition = HelpTextPosition;

    /**
     * Optional number of rows to grow the textarea to.
     * Only effective for values greater than 3.
     */
    public readonly autoGrow = input(0);

    /**
     * Optional number of rows in which textarea initializes
     */
    public readonly rows = input(2);

    /**
     * set the textarea to readonly
     */
    public readonly readonly = input(false);

    /**
     * Maxlength of input. This is a hard limit. The textarea will not allow anymore characters then this.
     * If you want to simply notify the user of a character limit (but still allow to type more), use the `maxCharacters` property
     */
    public readonly maxlength = input<number>();

    /**
     * Name of icon to show
     */
    protected readonly icon = input<AvailableIcons>();

    protected readonly formatDisplay = input<(value: any) => string>((value: any): string => value);

    /**
     * Native html autocomplete value. For a complete list see: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/autocomplete#values
     * This uses the [native browser autocomplete](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/autocomplete) which autofills forms based on information from your browser.
     */
    public readonly autocomplete = input('off', {
        transform: (value: string): string => {
            return !value || value.length === 0 ? 'off' : value;
        }
    });

    /**
     * Emits event if textarea is blurred.
     */
    public readonly textareaBlur = output<void>();

    /**
     * Emits event if icon is clicked
     */
    public readonly iconClick = output<string>();

    protected readonly textarea = viewChild<ElementRef>('textarea');

    protected isDisabled = false;

    protected hasScrollbar = false;

    private _maxValidator: ValidatorFn | undefined = undefined;

    private _value = '';
    public valueDisplay = '';

    public get value(): string {
        return this._value;
    }

    public set value(val: string) {
        this._value = val;
    }

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = `textarea-${Math.round(Math.random() * 10000000)}`;

    /**
     * Json object that contains default content messages from form element
     */
    protected content: { [key: string]: any };

    public constructor() {
        effect(() => this.setValidation());
        this.control && (this.control.valueAccessor = this);
        this.setDefaultContent(content);
    }

    get showCounter(): boolean {
        const max = this.maxCharacters() ?? 0;
        if (max <= 0) {
            return false;
        }
        const length = this.value?.length ?? 0;
        const threshold = Math.ceil(0.8 * max);
        return length >= threshold;
    }

    get limitReached(): boolean {
        const max = this.maxCharacters() ?? 0;
        return this.value?.length >= max;
    }

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = noop;

    /**
     * Remove previous validation and add new one.
     */
    private setValidation(): void {
        const control = this.control?.control;
        if (control) {
            this._maxValidator && control.removeValidators(this._maxValidator);
            const maxCharacters = this.maxCharacters();
            const maxCharactersSet = maxCharacters !== undefined;
            this._maxValidator = maxCharactersSet ? Validators.maxLength(this.maxCharacters()) : undefined;
            maxCharactersSet && control.addValidators([this._maxValidator]);
            control.updateValueAndValidity();
        }
    }

    /**
     * Emit click event
     */
    protected handleIconClick(event: Event): void {
        event.preventDefault();
        this.iconClick.emit(this.value);
        this.textarea().nativeElement.focus();
    }

    /**
     * Control value accessor function
     * Sets isDisabled state
     * @internal
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    /**
     * Control value accessor function
     * Write value
     * @internal
     */
    public writeValue(value: any): void {
        this.value = value;
        this.valueDisplay = value && this.formatDisplay() ? this.formatDisplay()(value) : value;
    }

    /**
     * Control value accessor function
     * Registers on change event
     * @internal
     */
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Control value accessor function
     * Registers on touched event
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Aria described by contains conditional fields that describe the input field
     */
    protected get ariaDescribedBy(): string {
        return [
            ...(this.description() ? [`${this.uniqueId}-description`] : []),
            ...(this.control ? [`${this.uniqueId}-errors`] : []),
            ...(this.helpText() ? [`${this.uniqueId}-help-text`] : [])
        ].join(' ');
    }

    /**
     * Set default content per component
     */
    private setDefaultContent(defaultContent: any): void {
        this.contentService.addContentItems(defaultContent);
        this.content = defaultContent;
    }

    /**
     * Handle change of input
     */
    protected onInputChange(event: Event): void {
        this.value = (event.target as HTMLInputElement).value;
        this.onChange(this.value);

        const textarea = this.textarea().nativeElement;
        this.hasScrollbar = textarea.clientHeight < textarea.scrollHeight;
    }

    /**
     * Format value on textareaBlur
     */
    protected onBlur(): void {
        this.textareaBlur.emit(undefined);
        this.onTouched();
    }

    protected get charactersUsed(): number {
        return this.value?.length || 0;
    }

    protected get characterLimitExceeded(): boolean {
        return this.charactersUsed > (this.maxCharacters() || Number.MAX_SAFE_INTEGER);
    }
}
